import { generateFilteredPayloads, generatePayloadLineData } from './utils';
/** CUSTOM UI Components */
import * as CustomTooltipUI from './CustomTooltipUI';
import { DATA_KEY } from '../constants/utils';
import moment from 'moment';
import {
  ChartNumberType,
  IDisplayedMeasure,
  IDisplayedSlice,
} from '../interfaces';
import { CHART_TYPES } from '../constants/chartTypes';
import { TooltipProps } from 'recharts';
import {
  DEFAULT_TOOLTIP_CONTEXT,
  ITooltipContext,
} from './tooltipContextInterfaces';
import { IAnalysisColorConfig } from '../utils/analysisColorConfig';
import { attemptFormatDate } from '../../../utils/formatting';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import ThinDivider from '../../ThinDivider';

interface IProps {
  props: TooltipProps<number, string>;
  lines?: any;
  chartValues: any[];
  linesMeta: any;
  timezone: string;
  forceBothValues?: boolean;
  xAxisKey?: string;
  displayedMeasures?: IDisplayedMeasure[];
  displayedSlices?: IDisplayedSlice[];
  chartType: CHART_TYPES;
  tooltipContext?: ITooltipContext;
  additionalLabel?: string | null;
  colorConfig: IAnalysisColorConfig;
  isBreakdownOverall?: IBreakdownOverallStatus;
  tooltipPayloadContentRef: React.RefObject<HTMLDivElement>;
  chartNumberType?: ChartNumberType;
  isFunnelRelativeLabels?: boolean;
}

export type ICustomTooltipPropsOverride = Partial<IProps>;

export const SCROLLABLE_CONTENT_CLASS_NAME = 'custom-tooltip-content';

export default ({
  props,
  chartValues,
  linesMeta,
  timezone,
  forceBothValues,
  xAxisKey = DATA_KEY,
  displayedMeasures,
  displayedSlices,
  chartType,
  lines,
  tooltipContext = DEFAULT_TOOLTIP_CONTEXT,
  additionalLabel,
  colorConfig,
  isBreakdownOverall = IBreakdownOverallStatus.NoBreakdown,
  tooltipPayloadContentRef,
  chartNumberType = ChartNumberType.Number,
  isFunnelRelativeLabels,
}: IProps) => {
  const reverseTooltipItems = [
    CHART_TYPES.PERCENT_STACKED_AREA_CHART,
    CHART_TYPES.STACKED_COLUMN_CHART,
  ].includes(chartType);
  const isRetentionTooltip =
    chartType &&
    [CHART_TYPES.RETENTION_BAR, CHART_TYPES.RETENTION_LINE].includes(chartType);
  const { payload, label: propLabel } = props;
  if (!payload) {
    return;
  }
  const xAxisLabel: string | undefined = propLabel as string | undefined;

  /**
   * construct overall line in area and stacked column chart.
   * The first (left most) line(s) (can be two in comparison mode)
   * will be hidden in chart, but we need to show it in tooltip
   */
  const filteredPayloads = generateFilteredPayloads({
    payload,
    chartType,
    lines,
    linesMeta,
    chartNumberType,
  });
  // `xAxisLabel` represents the X-Axis value. Normally it is a date in the M/D/YYYY format, unless:
  // * it is a single date Funnel(Conversion) chart (the X-Axis is the slice name)
  // * it is Funnel(Frequency) or Funnel(Time to Convert)
  // So, only when the X-Axis key is `date` it makes sense to try to display timezone and
  // for that matter, to parse the xAxisLabel as date.
  // Bear in mind that in All Time mode, the xAxisLabel is ("M/D/YYYY ~ M/D/YYYY").
  // Moment parses this string correctly (takes into account the first date)
  let shortTimeZone: string | null = null;
  if (xAxisKey === DATA_KEY && timezone && xAxisLabel) {
    shortTimeZone = moment.tz(timezone)?.zoneAbbr();
  }

  if (filteredPayloads && reverseTooltipItems) {
    filteredPayloads.reverse();
  }

  const displayPayloads = () => {
    return filteredPayloads?.map((result, index) => {
      const { lineValue, color } = generatePayloadLineData({
        linesMeta,
        resultLine: result,
        chartValues,
        xAxisKey,
        displayedMeasures: displayedMeasures || [],
        displayedSlices: displayedSlices ?? [],
        isRetentionTooltip,
        forceBothValues,
        chartType,
        tooltipContext,
        colorConfig,
        isBreakdownOverall,
        isFunnelRelativeLabels,
      });

      return (
        <CustomTooltipUI.ValueWrapper
          $color={color}
          key={result.dataKey + '-' + index}
        >
          <CustomTooltipUI.LineValueWrapper>
            {lineValue}
          </CustomTooltipUI.LineValueWrapper>
        </CustomTooltipUI.ValueWrapper>
      );
    });
  };

  return (
    <CustomTooltipUI.CustomTooltipContainer>
      <CustomTooltipUI.TooltipHeader>
        <CustomTooltipUI.TooltipHeaderMain>
          {additionalLabel && (
            <CustomTooltipUI.TooltipHeaderSecondary>
              {additionalLabel}
            </CustomTooltipUI.TooltipHeaderSecondary>
          )}
          <CustomTooltipUI.TooltipHeaderMainInfo>
            {xAxisLabel && <span>{attemptFormatDate(xAxisLabel)} </span>}
            <span>{shortTimeZone}</span>
          </CustomTooltipUI.TooltipHeaderMainInfo>
        </CustomTooltipUI.TooltipHeaderMain>
      </CustomTooltipUI.TooltipHeader>
      <ThinDivider layout="horizontal" size="full" />
      {/* Work with <ChartTooltipWheelListener />, Recharts always render the tooltip component,
          but only display when payloads are present. Therefore we need to check if payload is present
          and if so, provide className for the eventListener to work. */}
      {filteredPayloads.length > 0 && (
        <CustomTooltipUI.TooltipContent
          className={SCROLLABLE_CONTENT_CLASS_NAME}
          ref={tooltipPayloadContentRef}
        >
          {displayPayloads()}
        </CustomTooltipUI.TooltipContent>
      )}
    </CustomTooltipUI.CustomTooltipContainer>
  );
};
